import React, { forwardRef } from 'react'

import { useFormValueAndOnChange, useOnFormValidate } from 'admin/components/Form/FormProvider'
import { getComponentName } from 'admin/helpers'

type Option = {
  label: string
  value: any
}

type Props = {
  formKey: string
  required?: boolean
  value?: any
  onChange?: (event: any) => void
  validation?: any
  onValidationChange?: (event: any) => void
  persistOnUnmount?: boolean
  onValidate?: (event: any) => void
  label?: string
  onBlur?: (event: any) => void
  loadOptions?: (query: any) => Promise<any>
  defaultOptions?: Array<any>
  options?: Array<string> | Array<Option>
  personId?: string
  optional?: boolean
  showDefault?: boolean
}

type Options = {
  renderWhenHidden?: boolean
}

const withFormValue = (Component: React.ElementType<any>, options: Options = {}) => {
  const { renderWhenHidden } = options

  const FormValue = forwardRef<typeof Component, Props>((props, ref) => {
    const {
      formKey,
      required,
      value: passedValue,
      onChange: passedOnChange,
      validation: passedValidation,
      onValidationChange: passedOnValidationChange,
      persistOnUnmount,
      onValidate,
      label,
      onBlur: passedOnBlur,
      ...rest
    } = props

    const { value, onChange, onValidationChange, isHidden } = useFormValueAndOnChange({
      formKey,
      value: passedValue,
      onChange: passedOnChange,
      onValidationChange: passedOnValidationChange,
      persistOnUnmount,
    })

    const { meta, onBlur } = useOnFormValidate({
      value,
      formKey,
      label,
      required,
      onValidate,
      onValidationChange,
      onBlur: passedOnBlur,
    })

    const hide = !renderWhenHidden && isHidden

    return hide ? null : (
        <Component
          formKey={formKey}
          label={label}
          meta={meta}
          onBlur={onBlur}
          onChange={onChange}
          ref={ref}
          required={required}
          value={value}
          {...rest}
        />
      )
  })

  FormValue.displayName = getComponentName(Component, 'withFormValue')

  return FormValue
}

export default withFormValue
